/**
 * Split the experiences array into two non-overlapping pools — one for the
 * "Places to visit" row and one for the "Things to do" row — so the same
 * card can never appear in both sections.
 *
 * Cards eligible for ONLY one row are assigned to that row.
 * Cards eligible for BOTH rows are alternated: 1st overlap → Places,
 *   2nd → Things, 3rd → Places, …
 *
 * The alternation walks the input array in its natural order (the API
 * returns experiences sorted by `created_at desc`), so the assignment is
 * stable across renders and language switches — same input array, same
 * partition every time.
 */

// UI category slugs eligible for each row. Keep these in sync with the
// matching `*_CATEGORIES` lists in PlacesToVisitSection / WhatToDoSection.
// Travel-essentials cards (internal slug `transfers` / UI alias
// `rental_transfers`) are deliberately EXCLUDED from both homepage rows
// and the Top Experiences row — they only surface on their dedicated
// `/Explore?category=travel-essentials` listing page.
export const PLACES_CATEGORIES = [
  'tours_sightseeing', 'romantic', 'culture_arts', 'hotels',
];
export const THINGS_TO_DO_CATEGORIES = [
  'tours_sightseeing', 'food_wine', 'water_activities', 'nightlife',
  'culture_arts', 'romantic',
];

// Historical DB-slug aliases — same map both sections already use.
const UI_TO_DB_SLUG = {
  tours_sightseeing: 'tours',
  food_wine: 'gastronomy',
  water_activities: 'water_sports',
  rental_transfers: 'transfers',
  hotels: 'beaches',
};

// Cards tagged with the travel-essentials category (internal slug
// `transfers`, UI alias `rental_transfers`, URL alias `travel-essentials`)
// must NEVER appear in any homepage row, even when they also carry
// another category. They're confined to their own filter page.
const EXCLUDED_CATEGORIES = new Set([
  'transfers', 'rental_transfers', 'travel-essentials',
]);

function hasExcludedCategory(exp) {
  if (!exp.categories || !exp.categories.length) return false;
  return exp.categories.some((cat) => EXCLUDED_CATEGORIES.has(cat));
}

function matchesCategory(exp, uiSlugs) {
  if (!exp.categories || !exp.categories.length) return false;
  return exp.categories.some((cat) =>
    uiSlugs.some((ui) => cat === ui || cat === UI_TO_DB_SLUG[ui]),
  );
}

/**
 * Returns `{ placesPool, thingsPool }` — two arrays of experiences whose
 * IDs are mutually exclusive.
 */
export function splitHomepageRows(experiences = []) {
  const placesPool = [];
  const thingsPool = [];
  let overlapToggle = 0; // 0 → Places, 1 → Things

  for (const exp of experiences) {
    if (exp.limited_event) continue;
    if (hasExcludedCategory(exp)) continue;
    const inPlaces = matchesCategory(exp, PLACES_CATEGORIES);
    const inThings = matchesCategory(exp, THINGS_TO_DO_CATEGORIES);

    if (inPlaces && inThings) {
      if (overlapToggle === 0) placesPool.push(exp);
      else thingsPool.push(exp);
      overlapToggle = 1 - overlapToggle;
    } else if (inPlaces) {
      placesPool.push(exp);
    } else if (inThings) {
      thingsPool.push(exp);
    }
  }
  return { placesPool, thingsPool };
}
